import {
  SP_HOME_GET_ACCESS_STATUS,
  SP_HOME_GET_ECARD_PROFILE,
  SP_HOME_GET_ECARD_QR,
  SP_HOME_GET_FEED,
  SP_HOME_GET_LECTURES,
  SP_HOME_GET_OWN_PROFILE,
  SP_HOME_GET_STATS,
  SP_HOME_GET_SUBJECTS,
  SP_HOME_GET_TODAY_ATTENDANCE,
  SP_HOME_GET_TODAY_SUBJECT_ATTENDANCE,
} from './ActionTypes/ApiActionTypes'

/** Mobile app home: attendance / performance stats of one month. */
const API_HOME_STATS = 'home/stats'
/** Mobile app home: attention-required / upcoming / past feed. */
const API_HOME_FEED = 'home/feed'
/** Mobile app: the child's subjects (feed subject filter). */
const API_USER_SUBJECTS = 'user/subjects'
/** Mobile app: recorded / live lectures of the child's subjects. */
const API_LECTURES = 'lecture'
/** Daily (campus) attendance rows between two dates. */
const API_ATTENDANCE_INDIVIDUAL = 'attendance/individual'
/** Subject attendance rows between two dates. */
const API_ATTENDANCE_SUBJECT_INDIVIDUAL = 'attendance/subject-individual'
/** Fee-defaulter / restricted-login lock of the family portal. */
const API_ACCESS_STATUS = 'fee/portal/access-status'
/** Mobile app: the signed-in user's own profile (student e-card, profile cards). */
const API_PROFILE_DETAILS = 'user/profile-details'
/** Mobile app: a parent's child profile (the saga adds `childId`). */
const API_CHILD_PROFILE = 'user/child-profile'
/** QR image of the student e-card (the web has no QR renderer). */
const API_ECARD_QR = 'user/e-card/qr'

/**
 * @typedef {(res: any) => void} Callback
 */

/**
 * JSON POST action; the saga appends `childId`/`parentId` (the parent's
 * selected child, or the student themselves).
 * @param {string} type
 * @param {string} apiUrl
 * @param {Record<string, unknown>} body
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
const post = (type, apiUrl, body, onSuccess, onFailure) => ({
  type,
  payload: {
    onSuccess,
    onFailure,
    apiUrl,
    getString: '',
    useBaseURL: true,
    requestType: 'POST',
    header: 'application/json',
    body,
    reduxActionType: '',
  },
})

/**
 * Month stats (same call as the mobile dashboard). Resolves with `{result:
 * HomeStats}`: attendance count, homework + test performance, this week's
 * attendance and the per-subject performance breakdown.
 * @param {{month: string, year?: number}} period three-letter lower-case month
 *   (`sep`) and, optionally, its year (defaults to the current year on the API)
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getHomeStats = (period, onSuccess, onFailure) =>
  post(
    SP_HOME_GET_STATS,
    API_HOME_STATS,
    {month: period.month, year: period.year},
    onSuccess,
    onFailure
  )

/**
 * One page of the home feed (same body as the mobile dashboard). Resolves
 * with `{result: {data: FeedItem[], totalCount, numberOfPages}}`.
 * @param {{type: 'ATTENTION_REQUIRED'|'UPCOMING'|'PAST', page: number, pageSize?: number, filters?: object}} body
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getHomeFeed = (body, onSuccess, onFailure) =>
  post(SP_HOME_GET_FEED, API_HOME_FEED, body, onSuccess, onFailure)

/**
 * The child's active subjects. Resolves with `{result: {data: [{subjectId, name}]}}`.
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getHomeSubjects = (onSuccess, onFailure) =>
  post(SP_HOME_GET_SUBJECTS, API_USER_SUBJECTS, {search: {status: 1}}, onSuccess, onFailure)

/**
 * Recorded (`RECORDED`) or live (`LIVE`) lectures of the child's subjects,
 * newest first. Resolves with `{result: {data: LectureItem[], totalCount}}`.
 * @param {'RECORDED'|'LIVE'} type
 * @param {number} pageSize
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getHomeLectures = (type, pageSize, onSuccess, onFailure) =>
  post(SP_HOME_GET_LECTURES, API_LECTURES, {type, page: 1, pageSize}, onSuccess, onFailure)

/**
 * Campus attendance of one day. Resolves with `{result: {data: row[]}}`.
 * @param {string} date `YYYY-MM-DD`
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getTodayAttendance = (date, onSuccess, onFailure) =>
  post(
    SP_HOME_GET_TODAY_ATTENDANCE,
    API_ATTENDANCE_INDIVIDUAL,
    {start_date: date, end_date: date},
    onSuccess,
    onFailure
  )

/**
 * Subject attendance of one day. Resolves with `{result: {data: row[]}}`.
 * @param {string} date `YYYY-MM-DD`
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getTodaySubjectAttendance = (date, onSuccess, onFailure) =>
  post(
    SP_HOME_GET_TODAY_SUBJECT_ATTENDANCE,
    API_ATTENDANCE_SUBJECT_INDIVIDUAL,
    {start_date: date, end_date: date},
    onSuccess,
    onFailure
  )

/**
 * Whether the portal is locked for the student / the parent's selected child
 * (fee defaulter or restricted login). Resolves with `{result: {blocked,
 * reason, message, studentUserId}}`.
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getPortalAccessStatus = (onSuccess, onFailure) =>
  post(SP_HOME_GET_ACCESS_STATUS, API_ACCESS_STATUS, {}, onSuccess, onFailure)

/**
 * The profile the student e-card shows — the mobile StudentECard calls:
 * `POST user/child-profile` for a parent (the saga adds the selected
 * `childId`), `GET user/profile-details` for a student. Resolves with
 * `{result: {name, regNo, class, profilePicture, uuid, …}}`.
 * @param {boolean} forParent the viewer is a parent
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getECardProfile = (forParent, onSuccess, onFailure) =>
  forParent
    ? post(SP_HOME_GET_ECARD_PROFILE, API_CHILD_PROFILE, {}, onSuccess, onFailure)
    : {
        type: SP_HOME_GET_ECARD_PROFILE,
        payload: {
          onSuccess,
          onFailure,
          apiUrl: API_PROFILE_DETAILS,
          getString: '',
          useBaseURL: true,
          requestType: 'GET',
          header: 'application/json',
          reduxActionType: '',
        },
      }

/**
 * The signed-in student's / parent's own profile — the mobile Profile
 * screen's `GET user/profile-details` (subjects, parents/guardians, children).
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getOwnFamilyProfile = (onSuccess, onFailure) => ({
  type: SP_HOME_GET_OWN_PROFILE,
  payload: {
    onSuccess,
    onFailure,
    apiUrl: API_PROFILE_DETAILS,
    getString: '',
    useBaseURL: true,
    requestType: 'GET',
    header: 'application/json',
    reduxActionType: '',
  },
})

/**
 * QR code of the student e-card's back. Resolves with `{result: {uuid,
 * qrCode}}` (`qrCode` is a PNG data URL, '' when the student has no uuid).
 * @param {Callback} onSuccess
 * @param {Callback} [onFailure]
 */
export const getECardQr = (onSuccess, onFailure) =>
  post(SP_HOME_GET_ECARD_QR, API_ECARD_QR, {}, onSuccess, onFailure)
